/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of Zuum Transportation Inc.
 #*
 #* Copyright Zuum 2015-2021. All Rights Reserved.
 #*
 #*===================================================================
 */

import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
// Models
import {
  ApiResponse,
  CLIENT_PLAN_TYPES,
  GlobalPriceConfiguration,
  GLOBAL_PRICE_CONFIGURATION_CLIENT_TYPE,
  PriceConfigDescription,
  PRICE_CONFIGURATION_NAMES,
} from '@core/models';
// Rxjs
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
// Services
import { ApiService } from 'app/services/api.service';

@Injectable({
  providedIn: 'root',
})
export class GlobalPriceConfigurationService {
  activePageIndex: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  constructor(
    private http: HttpClient,
    private apiService: ApiService,
    private matIconReqistry: MatIconRegistry,
    private domSanitizer: DomSanitizer
  ) {}

  getGlobalPricingConfigurationByClientType(
    clientType: GLOBAL_PRICE_CONFIGURATION_CLIENT_TYPE
  ): Observable<ApiResponse<GlobalPriceConfiguration[]>> {
    return this.http
      .get<ApiResponse>(`${this.apiService.superAdminApi}/global-price-configuration/by-client-type/${clientType}`)
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  updateGlobalConfigById(config: Partial<GlobalPriceConfiguration>): Observable<ApiResponse<GlobalPriceConfiguration>> {
    return this.http
      .put<
        ApiResponse<GlobalPriceConfiguration>
      >(`${this.apiService.superAdminApi}/global-price-configuration/${config.id}`, config)
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  batchUpdateGlobalConfigs(configs: Partial<GlobalPriceConfiguration>[]): Observable<ApiResponse> {
    return this.http
      .post<ApiResponse>(`${this.apiService.superAdminApi}/global-price-configuration/batch-update`, configs)
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  deleteGlobalConfigById(id: number): Observable<ApiResponse> {
    return this.http
      .delete<ApiResponse>(`${this.apiService.superAdminApi}/global-price-configuration/${id}`)
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  addGlobalConfig(config: GlobalPriceConfiguration): Observable<ApiResponse> {
    return this.http
      .post<ApiResponse>(`${this.apiService.superAdminApi}/global-price-configuration/new-config`, { config })
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  getPriceConfigDescriptionByName(name: PRICE_CONFIGURATION_NAMES): Observable<ApiResponse> {
    return this.http
      .get<ApiResponse>(`${this.apiService.superAdminApi}/price-config-description/by-name/${name}`)
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  addPriceConfigDescription(config: PriceConfigDescription): Observable<ApiResponse> {
    return this.http
      .post<ApiResponse>(`${this.apiService.superAdminApi}/price-config-description/new-config`, { config })
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  getPlanIconByType(planType: CLIENT_PLAN_TYPES): string {
    if (
      planType === CLIENT_PLAN_TYPES.TRUCKSTOP_AUTOMATED_BROKER ||
      planType === CLIENT_PLAN_TYPES.TRUCKSTOP_CAPACITY_PRO
    ) {
      return 'leaf-icon';
    } else if (
      planType === CLIENT_PLAN_TYPES.TRUCKSTOP_AUTOMATED_BROKER_PRO ||
      planType === CLIENT_PLAN_TYPES.TRUCKSTOP_BUSINESS
    ) {
      return 'team-icon';
    } else if (
      // planType === CLIENT_PLAN_TYPES.TRUCKSTOP_AUTOMATED_BROKER_ENTERPRISE ||
      planType === CLIENT_PLAN_TYPES.TRUCKSTOP_ENTERPRISE
    ) {
      return 'infinite-icon';
    } else {
      return 'leaf-icon';
    }
  }

  addPlansSvgIcons(): void {
    this.matIconReqistry.addSvgIcon(
      'leaf-icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/icons/settings/leaf_icon.svg')
    );
    this.matIconReqistry.addSvgIcon(
      'team-icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/icons/settings/team_icon.svg')
    );
    this.matIconReqistry.addSvgIcon(
      'infinite-icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/icons/settings/infinite_icon.svg')
    );
  }
}
